import type { FormApi } from '../../form-hooks/useFormApi';
import type { MediaFormSubmitFn } from '../../form/MediaForm/MediaForm';
import type {
  ContentObject,
  Media,
  WithOptionalInternalProps,
} from '../../lib/flotiq-client/types/api-response';
import type { ContentObjectFormAfterSubmit } from '../AddContentObject/AddContentObjectContainer';

import { use, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import deepEqual from 'deep-equal';
import { twMerge } from 'tailwind-merge';

import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { FormAddSidebarPanelEvent } from '../../lib/flotiq-plugins/plugin-events/FormAddSidebarPanelEvent';
import { getTestProps } from '../../lib/helpers';

import ContentObjectInformation from '../../components/ContentObjectInformation/ContentObjectInformation';
import ElementFromPlugin from '../../components/ElementFromPlugin/ElementFromPlugin';
import FormEditorsIndicator from '../../components/FormEditorsIndicator/FormEditorsIndicator';
import Heading from '../../components/Heading/Heading';
import Loader from '../../components/Loader/Loader';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarReconnectButton from '../../components/Topbar/buttons/TopbarReconnectButton';
import TopbarSaveAndLeaveButton from '../../components/Topbar/buttons/TopbarSaveAndLeaveButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';

import { useContentObject, useContentType } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import { useBacklinks } from '../../hooks/api/useBacklinks';
import useFirstLoading from '../../hooks/useFirstLoading';
import usePluginResults from '../../hooks/usePluginResults';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useWebsocketForForm from '../../hooks/useWebsocket/useWebsocketForForm';

import ContentObjectFormContext from '../../contexts/ContentObjectFormContext';
import DirtyHandlerContext from '../../contexts/DirtyHandlerContext';
import { ModalInstanceContext, useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';
import WebsocketContext from '../../contexts/WebsocketContext';

import { ClipboardIcon, WarningIcon } from '../../images/shapes';

import MediaForm, {
  getInitialMediaValues,
} from '../../form/MediaForm/MediaForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import PoppingPageLayout from '../../layout/PageLayout/PoppingPageLayout';
import ContentObjectBacklinks from '../AddContentObject/ContentObjectBacklinks/ContentObjectBacklinks';

interface EditMediaContainerProps {
  /**
   * Media id to edit.
   */
  id?: string;
  /**
   * On submit action callback
   */
  onSubmit?: ContentObjectFormAfterSubmit;
  /**
   * Page test id
   */
  testId?: string;
}

const EditMediaContainer = ({
  id,
  onSubmit,
  testId = '',
}: EditMediaContainerProps) => {
  const navigateOnSave = useRef<boolean>(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modal = useModals();
  const { permissions } = use(UserContext);
  const { buildUrlWithSpace } = useSelectedSpace();
  const { setDirty } = use(DirtyHandlerContext);

  const modalInstance = use(ModalInstanceContext);
  const inModal = !!modalInstance?.resolve;

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formState, setFormState] = useState<FormApi>();

  const { entity: mediaContentType } = useContentType('_media');

  const {
    entity: media,
    isLoading,
    status,
    errors,
    deleteEntity: deleteMediaObject,
    partialUpdateEntity: patchMediaObject,
    reload: reloadMediaObject,
  } = useContentObject('_media', id);

  const {
    yjsDocument,
    wsStatus,
    retryWsConnection,
    editors,
    awareness,
    waitForYjsSync,
  } = useWebsocketForForm('_media', id, reloadMediaObject);

  const isYjsDirty = useMemo(() => {
    if (wsStatus !== 'synced' || !yjsDocument || !media) return false;
    if (!yjsDocument.getMap('vals').size) return false;

    const currentValues = yjsDocument.getMap('vals').toJSON();
    const mediaToCompare = getInitialMediaValues(media as Media);

    return !deepEqual(currentValues, mediaToCompare);
  }, [media, yjsDocument, wsStatus]);

  const firstLoading = useFirstLoading(!isLoading || !id, id);

  useApiErrorsToast(errors);

  const handleDeleteMedia = useCallback(async () => {
    modal.deleting('delete-modal');
    try {
      const { body, status, ok } = await deleteMediaObject({
        contentTypeName: '_media',
      });
      if (!ok) {
        throw parseResponseErrors(body, status);
      }

      toast.success(t('Media.MediaManagement.DeleteSuccess'));
      setDirty(false);

      if (!inModal) navigate(buildUrlWithSpace('media'));
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
      } else {
        toast.error(
          error.message
            ? error.message
            : t('Media.MediaManagement.CouldntDelete'),
        );
      }
    }
  }, [
    modal,
    deleteMediaObject,
    t,
    setDirty,
    inModal,
    navigate,
    buildUrlWithSpace,
  ]);

  const deleteMedia = useCallback(async () => {
    setIsDeleting(true);
    await modal.delete(
      <div className="dark:text-white">
        <Heading additionalClasses="dark:text-white" level={6}>
          {t('MediaEdit.ConfirmDelete')}
        </Heading>
        <p>{t('Media.MediaManagement.DeleteWarning')}</p>
      </div>,
      'delete-modal',
      () => handleDeleteMedia(),
    );
    setIsDeleting(false);
  }, [handleDeleteMedia, modal, t]);

  const { canDelete, canUpdate, canRead } = useMemo(
    () => permissions.getCoPermissions('_media') || {},
    [permissions],
  );

  const {
    backlinks,
    backlinkContentTypes,
    backlinkPagination,
    setBacklinksPage,
  } = useBacklinks(id, '_media');

  const updateMedia = useCallback<MediaFormSubmitFn>(
    async (values) => {
      try {
        const { status, body, ok } = await patchMediaObject({
          contentTypeName: '_media',
          ...values,
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('MediaEdit.Updated'));

        return [[body as Media, {}], false];
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [[values, {}], true];
        }
        toast.error(error.message || t('MediaEdit.CouldntUpdate'));
        return [[values, error.errors], true];
      }
    },
    [patchMediaObject, t],
  );

  const onFormSubmit: MediaFormSubmitFn = async (values) => {
    setIsSaving(true);
    const result = await updateMedia(values);
    setIsSaving(false);

    const [formResponse, hasErrors] = result;

    onSubmit?.({
      action: 'save',
      contentObject:
        formResponse[0] as WithOptionalInternalProps<ContentObject>,
      contentType: mediaContentType,
      errors: formResponse[1],
      success: !hasErrors,
    });

    if (!hasErrors && yjsDocument) {
      const newMedia = formResponse[0] as Media;
      yjsDocument
        .getMap('formState')
        .set('latestVersion', newMedia.internal?.latestVersion);
    }

    if (navigateOnSave.current && !hasErrors) {
      if (inModal) {
        modalInstance.resolve?.(formResponse[0]);
      } else {
        navigate(buildUrlWithSpace('media'));
      }
    }

    return result;
  };

  const showForm = id && media && !firstLoading && canRead && !waitForYjsSync;

  const emptyData = useMemo(() => {
    if (showForm) return null;
    if (firstLoading || waitForYjsSync) {
      return (
        <Loader
          size={'small'}
          type={'spinner-grid'}
          testId={testId ? `${testId}-loader` : ''}
        />
      );
    }
    if (!canRead) {
      toast.error(t('MediaEdit.CouldntFind', { id }));
    }
    return (
      <Heading
        level={2}
        additionalClasses={twMerge(
          'text-3xl md:text-4xl leading-8 dark:text-white',
        )}
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon
            className="text-red w-14 md:w-20 mb-3"
            title={t('MediaEdit.CouldntFind', { id })}
          />
          {status === 404 || !canRead
            ? t('MediaEdit.CouldntFind', { id })
            : t('MediaEdit.CouldntFetch')}
        </div>
      </Heading>
    );
  }, [showForm, firstLoading, canRead, testId, t, id, status, waitForYjsSync]);

  /**
   * @emits FlotiqPlugins."flotiq.form.sidebar-panel::add"
   */
  const pluginSidebarPanels = usePluginResults(
    'flotiq.form.sidebar-panel::add',
    FormAddSidebarPanelEvent,
    {
      contentType: mediaContentType,
      contentObject: media,
      disabled: isSaving || isDeleting,
      duplicate: false,
      create: false,
      form: formState,
    },
  );

  const contentObjectFormContextValue = useMemo(
    () => ({
      contentType: mediaContentType,
      initialData: media,
    }),
    [media, mediaContentType],
  );

  const isSaveAllowed = canUpdate && !!id;
  const isDataDirty = formState?.dirty || isYjsDirty;

  const LayoutComponent = inModal ? PoppingPageLayout : PageLayout;

  const onCancel = useCallback(() => {
    modalInstance.resolve?.(media);
  }, [media, modalInstance]);

  return (
    <LayoutComponent
      {...(inModal
        ? ({ onBack: onCancel } as any)
        : {
            page: 'media',
            breadcrumbs: (
              <TopbarBreadcrumbs
                parentTitle={t('Global.MediaLibrary')}
                parentLink={buildUrlWithSpace('media')}
              />
            ),
          })}
      title={t('MediaEdit.Edit')}
      buttonsDisabled={isSaving || isDeleting}
      buttons={
        <>
          <TopbarCancelButton
            isDirty={isDataDirty}
            {...(inModal
              ? { onClick: onCancel }
              : { link: buildUrlWithSpace('media') })}
          />
          <TopbarSaveButton
            form="media-form"
            isLoading={isSaving}
            actionDisallowed={!isSaveAllowed}
            navigateOnSave={navigateOnSave}
            disabled={!isDataDirty}
          />
          <TopbarActionMenu>
            <TopbarSaveAndLeaveButton
              form="media-form"
              actionDisallowed={!isSaveAllowed}
              navigateOnSave={navigateOnSave}
              disabled={!isDataDirty}
            />
            {id && !inModal && (
              <TopbarDeleteButton
                onClick={deleteMedia}
                actionDisallowed={!canDelete}
              />
            )}
          </TopbarActionMenu>
        </>
      }
      status={
        <>
          <FormEditorsIndicator editors={editors} />
          {(wsStatus === 'failed' || wsStatus === 'retrying') && (
            <TopbarReconnectButton
              onRetry={retryWsConnection}
              isRetrying={wsStatus === 'retrying'}
            />
          )}
        </>
      }
      testId={testId}
    >
      {showForm ? (
        <div className={predefinedLayoutClasses.withSidebar}>
          <div
            className={twMerge(
              predefinedLayoutClasses.leftColumn,
              'space-y-3 md:space-y-6',
            )}
          >
            <WebsocketContext.Provider
              value={{ yjsDocument, awareness, editors, isYjsDirty }}
            >
              <ContentObjectFormContext value={contentObjectFormContextValue}>
                <MediaForm
                  media={media as Media | null}
                  onSubmit={onFormSubmit}
                  disabled={isSaving || isDeleting || !canUpdate}
                  setFormState={setFormState}
                  {...getTestProps(testId, 'form', 'testId')}
                />
              </ContentObjectFormContext>
            </WebsocketContext.Provider>
          </div>
          <div className={predefinedLayoutClasses.rightColumn}>
            <ContentObjectInformation
              id={media.id}
              createdAt={media.internal?.createdAt}
              updatedAt={media.internal?.updatedAt}
              additionalElements={
                <div
                  className={twMerge(
                    'flex items-start text-slate-900 dark:text-slate-200',
                  )}
                >
                  <ClipboardIcon className="min-w-4 w-4 mr-3" />
                  <span>ID</span>
                  <span className="font-bold mx-1 break-all">{id}</span>
                </div>
              }
              additionalClasses="order-10"
              {...getTestProps(testId, 'info', 'testId')}
            />

            {backlinks?.length > 0 && (
              <ContentObjectBacklinks
                backlinks={backlinks}
                contentTypes={backlinkContentTypes}
                pagination={backlinkPagination}
                onPageChange={setBacklinksPage}
                additionalClasses="order-20"
                {...getTestProps(testId, 'backlinks', 'testId')}
              />
            )}

            {pluginSidebarPanels?.length > 0 && (
              <ElementFromPlugin results={pluginSidebarPanels} />
            )}
          </div>
        </div>
      ) : (
        <div className={predefinedLayoutClasses.whiteBox}>{emptyData}</div>
      )}
    </LayoutComponent>
  );
};

export default EditMediaContainer;
